import React, { useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

const CompanyCards = ({ companyData, onCompanyClick, isLoading }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [topN, setTopN] = useState(5);

    if (isLoading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '450px',
                flexDirection: 'column',
                gap: 2
            }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary">
                    Loading company data...
                </Typography>
            </Box>
        );
    }

    if (!companyData || (!companyData.converged?.length && !companyData.non_converged?.length)) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '450px',
                flexDirection: 'column',
                gap: 1
            }}>
                <Typography variant="body1" color="text.secondary">
                    No company data available
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    Apply filters to view company statistics
                </Typography>
            </Box>
        );
    }
    const sliceArr = (arr) => (topN === 'all' ? arr.slice(0, 25) : arr.slice(0, topN));

    const convHalf = topN === 'all' ? 13 : Math.ceil(topN / 2);
    const ncHalf = topN === 'all' ? 12 : Math.floor(topN / 2);
    const sliceHalf = (arr, n) => arr.slice(0, n);

    const convergedForAll = companyData.converged ? sliceHalf(companyData.converged, convHalf) : [];
    const nonConvergedForAll = companyData.non_converged ? sliceHalf(companyData.non_converged, ncHalf) : [];

    const mixedCompanies = [
        ...convergedForAll.map(c => ({ ...c, _type: 'converged' })),
        ...nonConvergedForAll.map(c => ({ ...c, _type: 'non_converged' })),
    ];

    const fullConverged = companyData.converged ? sliceArr(companyData.converged) : [];
    const fullNonConverged = companyData.non_converged ? sliceArr(companyData.non_converged) : [];

    let currentCompanies;
    let currentType;
    if (activeTab === 0) {
        currentCompanies = mixedCompanies;
        currentType = 'all';
    } else if (activeTab === 1) {
        currentCompanies = fullConverged.map(c => ({ ...c, _type: 'converged' }));
        currentType = 'convergence';
    } else {
        currentCompanies = fullNonConverged.map(c => ({ ...c, _type: 'non_converged' }));
        currentType = 'nonConvergence';
    }

    const isAllTab = activeTab === 0;

    const colorPalette = [
        '#D0CEFB', '#FEB4AA', '#B8DFFE', '#FCE7B8', '#FCD4E3',
        '#C5E1A5', '#FFE082', '#FFAB91', '#CE93D8', '#90CAF9',
        '#A5D6A7', '#FFCC80', '#EF9A9A', '#B39DDB', '#81D4FA',
        '#C5E1A5', '#FFD54F', '#BCAAA4', '#E1BEE7', '#80DEEA',
        '#DCEDC8', '#FFF59D', '#FFCCBC', '#D1C4E9', '#B2EBF2'
    ];

    // Prepare pie chart data with enhanced hover effects
    const pieChartData = {
        series: currentCompanies.map(c => c.count || 0),
        options: {
            chart: {
                type: 'pie',
                height: topN > 10 ? 500 : 380,
                events: {
                    dataPointSelection: (event, chartContext, config) => {
                        const company = currentCompanies[config.dataPointIndex];
                        if (company) {
                            onCompanyClick(company, currentType);
                        }
                    }
                },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                    animateGradually: {
                        enabled: true,
                        delay: 150
                    },
                    dynamicAnimation: {
                        enabled: true,
                        speed: 350
                    }
                }
            },
            labels: currentCompanies.map(c => c.companyName || c.name || 'Unknown'),
            colors: colorPalette.slice(0, currentCompanies.length),
            legend: {
                position: 'bottom',
                fontSize: '11px',
                itemMargin: {
                    horizontal: 6,
                    vertical: 3
                },
                markers: {
                    width: 12,
                    height: 12,
                    radius: 2
                }
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '10px',
                    fontWeight: 600,
                    colors: ['#000']
                },
                formatter: function (val) {
                    return val.toFixed(1) + '%';
                },
                dropShadow: {
                    enabled: false
                }
            },
            states: {
                hover: {
                    filter: {
                        type: 'lighten',
                        value: 0.15
                    }
                },
                active: {
                    allowMultipleDataPointsSelection: false,
                    filter: {
                        type: 'darken',
                        value: 0.2
                    }
                }
            },
            plotOptions: {
                pie: {
                    expandOnClick: true,
                    customScale: 1,
                    dataLabels: {
                        offset: 0,
                        minAngleToShowLabel: 10
                    }
                }
            },
            tooltip: {
                enabled: true,
                theme: 'light',
                style: {
                    fontSize: '12px',
                    fontFamily: 'inherit'
                },
                custom: function ({ seriesIndex }) {
                    const company = currentCompanies[seriesIndex];
                    const disposalRate = company.disposalRate || 0;
                    const disposedCount = company.disposedCount || 0;
                    const avgDays = company.avgDisposalDays ? company.avgDisposalDays.toFixed(1) : 'N/A';
                    const feedbackDist = company.feedbackDistribution || {};

                    const ratingMeta = [
                        { key: '5', label: 'Excellent', color: '#2e7d32' },
                        { key: '4', label: 'Very Good', color: '#558b2f' },
                        { key: '3', label: 'Good', color: '#f9a825' },
                        { key: '2', label: 'Poor', color: '#e65100' },
                        { key: '1', label: 'Very Bad', color: '#c62828' },
                        { key: '0', label: 'No Rating', color: '#9e9e9e' },
                    ];

                    const totalFeedback = Object.values(feedbackDist).reduce((s, v) => s + (v || 0), 0);

                    const feedbackRows = ratingMeta
                        .filter(r => (feedbackDist[r.key] || 0) > 0)
                        .map(r => {
                            const cnt = feedbackDist[r.key] || 0;
                            const pct = totalFeedback > 0 ? ((cnt / totalFeedback) * 100).toFixed(1) : '0.0';
                            return `
                                <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                                    <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${r.color};flex-shrink:0;"></span>
                                    <span style="flex:1;font-size:11px;color:#444;">${r.label}</span>
                                    <div style="width:64px;background:#eee;border-radius:3px;height:6px;overflow:hidden;">
                                        <div style="width:${pct}%;background:${r.color};height:100%;border-radius:3px;"></div>
                                    </div>
                                    <span style="font-size:11px;font-weight:600;color:#333;min-width:34px;text-align:right;">${pct}%</span>
                                </div>
                            `;
                        }).join('');

                    const companyName = company.companyName || company.name || '';

                    return `
                        <div style="padding:10px 12px;min-width:220px;font-family:inherit;">
                            <div style="font-weight:700;font-size:12px;color:#1a1a1a;margin-bottom:6px;border-bottom:1px solid #eee;padding-bottom:6px;display:flex;align-items:center;gap:6px;">
                                ${isAllTab ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;flex-shrink:0;background:${company._type === 'converged' ? '#4caf50' : '#ff9800'};"></span>` : ''}
                                ${companyName}
                            </div>
                            <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
                                <span style="font-size:11px;color:#666;">Total</span>
                                <span style="font-size:11px;font-weight:600;">${(company.count || 0).toLocaleString()}</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
                                <span style="font-size:11px;color:#666;">Disposed</span>
                                <span style="font-size:11px;font-weight:600;">${disposedCount.toLocaleString()}</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
                                <span style="font-size:11px;color:#666;">Disposal Rate</span>
                                <span style="font-size:11px;font-weight:600;">${disposalRate.toFixed(2)}%</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;margin-bottom:${feedbackRows ? 10 : 0}px;">
                                <span style="font-size:11px;color:#666;">Avg. Disposal</span>
                                <span style="font-size:11px;font-weight:600;">${avgDays} days</span>
                            </div>
                            ${feedbackRows ? `
                            <div style="border-top:1px solid #eee;padding-top:8px;">
                                <div style="font-size:11px;font-weight:700;color:#555;margin-bottom:6px;">Feedback Distribution</div>
                                ${feedbackRows}
                            </div>` : ''}
                        </div>
                    `;
                }
            },
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: {
                        height: 300
                    },
                    legend: {
                        position: 'bottom',
                        fontSize: '10px'
                    }
                }
            }]
        }
    };

    // Avg Disposal Time chart
    const disposalRateChartData = {
        series: [
            {
                name: 'Avg. Disposal Days',
                data: currentCompanies.map(c =>
                    parseFloat((c.avgDisposalDays || 0).toFixed(2))
                ),
            },
        ],

        options: {
            chart: {
                type: 'bar',
                height: topN > 10 ? 500 : 380,
                toolbar: {
                    show: false,
                },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                },
            },

            plotOptions: {
                bar: {
                    horizontal: true,
                    distributed: true,
                    barHeight: topN > 10 ? '50%' : '65%',
                    dataLabels: {
                        position: 'top',
                    },
                },
            },

            colors: colorPalette.slice(0, currentCompanies.length),

            dataLabels: {
                enabled: true,
                offsetX: 36,
                style: {
                    fontSize: '11px',
                    fontWeight: 600,
                    colors: ['#333'],
                },
                formatter: function (val) {
                    return val + ' d';
                },
            },

            // ✅ Remove all grid/border lines
            grid: {
                xaxis: {
                    lines: {
                        show: false,
                    },
                },
                yaxis: {
                    lines: {
                        show: false,
                    },
                },
            },

            // ✅ Remove bar stroke/border
            stroke: {
                show: false,
                width: 0,
            },

            xaxis: {
                categories: currentCompanies.map(
                    c => c.companyName || c.name || 'Unknown'
                ),

                labels: {
                    show: false,
                },

                axisBorder: {
                    show: true,
                },

                axisTicks: {
                    show: true,
                },
            },

            yaxis: {
                labels: {
                    style: {
                        fontSize: '10px',
                        fontWeight: 500,
                    },

                    formatter: function (val) {
                        return val; // no truncation
                    },
                },

                axisBorder: {
                    show: false,
                },

                axisTicks: {
                    show: false,
                },
            },

            states: {
                hover: {
                    filter: {
                        type: 'darken',
                        value: 0.15,
                    },
                },
            },

            tooltip: {
                enabled: true,
                theme: 'light',

                y: {
                    formatter: function (val, { dataPointIndex }) {
                        const company = currentCompanies[dataPointIndex];
                        const disposalRate = company.disposalRate || 0;

                        const typeBadge = isAllTab
                            ? `<span style="
                display:inline-block;
                width:10px;
                height:10px;
                border-radius:50%;
                margin-left:6px;
                flex-shrink:0;
                background:${company._type === 'converged' ? '#4caf50' : '#ff9800'
                            };
              "></span>`
                            : '';

                        return `
            <div style="padding:8px;">
              <div style="margin-bottom:4px;display:flex;align-items:center;">
                <strong>Avg. Disposal Time:</strong>&nbsp;${val} days${typeBadge}
              </div>

              <div style="margin-bottom:4px;">
                <strong>Disposed:</strong> ${(company.disposedCount || 0).toLocaleString()}
              </div>

              <div style="margin-bottom:4px;">
                <strong>Total:</strong> ${(company.count || 0).toLocaleString()}
              </div>

              <div>
                <strong>Disposal Rate:</strong> ${disposalRate.toFixed(2)}%
              </div>
            </div>
          `;
                    },
                },
            },

            legend: {
                show: false,
            },
        },
    };
    return (
        <Box sx={{ p: 2 }}>
            {/* Header with Title and Top N Selector */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Top Companies Analysis
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Show Top</InputLabel>
                    <Select
                        value={topN}
                        label="Show Top"
                        onChange={(e) => setTopN(e.target.value)}
                    >
                        <MenuItem value={5}>Top 5</MenuItem>
                        <MenuItem value={10}>Top 10</MenuItem>
                        <MenuItem value={20}>Top 20</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Tabs — All / Convergence / Non-Convergence */}
            <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                centered
                sx={{
                    mb: 2,
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 500
                    },
                    '& .Mui-selected': {
                        fontWeight: 600
                    }
                }}
            >
                <Tab
                    label={`All (${mixedCompanies.length})`}
                    // icon={<AllInclusiveIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                />
                <Tab
                    label={`Convergence (${fullConverged.length})`}
                    // icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                />
                <Tab
                    label={`Non-Convergence (${fullNonConverged.length})`}
                    // icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                />
            </Tabs>

            {/* Charts Container - Dynamic Height */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                minHeight: '400px'
            }}>
                {/* Pie Chart - Complaint Distribution */}
                <Paper elevation={2} sx={{ flex: 1, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                        Complaint Distribution
                    </Typography>
                    <ReactApexChart
                        options={pieChartData.options}
                        series={pieChartData.series}
                        type="pie"
                        height={topN > 10 ? 500 : 380}
                    />
                </Paper>

                {/* Bar Chart - Avg Disposal Days */}
                <Paper elevation={2} sx={{ flex: 1, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
                        Avg. Disposal Time (Days)
                    </Typography>
                    <ReactApexChart
                        options={disposalRateChartData.options}
                        series={disposalRateChartData.series}
                        type="bar"
                        height={topN > 10 ? 500 : 380}
                    />
                </Paper>
            </Box>
        </Box>
    );
};

export default CompanyCards;
